export function countryLabel(code: string) {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
}

const CURRENCY_WORDS: Record<string, string> = {
  CAD: "Canadian dollars",
  GBP: "British pounds",
  USD: "US dollars",
  EUR: "euros",
  AUD: "Australian dollars",
  NZD: "New Zealand dollars",
  JPY: "yen",
  CHF: "Swiss francs",
};

export function currencyWords(code?: string): string {
  const cur = (code || "").toUpperCase();
  if (!cur) return "";
  return CURRENCY_WORDS[cur] || cur;
}

export function destinationLabel(code: string, currency?: string) {
  const cur = (currency || "").toUpperCase();
  const words = currencyWords(cur);
  if (words) return `Asked to switch to ${cur || words}`;
  if (code === "CA") return "Asked to switch to CAD";
  if (code === "GB") return "Asked to switch to GBP";
  return "Asked to switch currency";
}

/** R6: always en-US digits (10,000 not 10.000). */
export function formatNumber(n: number): string {
  return Number(n || 0).toLocaleString("en-US");
}

/** R6: “Oct 28, 7:27 AM” - pass shop timezone when known. */
export function formatDateTime(
  iso?: string | Date | null,
  timeZone?: string | null,
): string {
  if (!iso) return "";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
}

/** Prefer custom domain over *.myshopify.com / opaque handles (R8). */
export function storefrontHost(opts: {
  primaryDomain?: string | null;
  shopDomain?: string | null;
  myshopifyDomain?: string | null;
}): string {
  const candidates = [
    opts.primaryDomain,
    opts.shopDomain,
    opts.myshopifyDomain,
  ]
    .map((s) =>
      String(s || "")
        .replace(/^https?:\/\//i, "")
        .replace(/\/$/, "")
        .toLowerCase(),
    )
    .filter(Boolean);
  const custom = candidates.find((h) => h && !h.endsWith(".myshopify.com"));
  if (custom) return custom;
  return candidates[0] || "This store";
}

export function planLabel(plan?: string) {
  const p = (plan || "").toLowerCase();
  if (p.includes("multi")) return "Multi-store";
  if (p.includes("starter")) return "Starter";
  if (p.includes("scale")) return "Scale";
  return "Free";
}

/** Plain-English “what you can do” for new merchants (US). */
export function planCapabilityCopy(plan?: string, planKey?: string): {
  label: string;
  can: string[];
  tip: string;
} {
  const label = planLabel(plan || planKey);
  if (label === "Scale") {
    return {
      label,
      can: [
        "Ask first or send automatically (unlimited)",
        "Other websites + block countries",
        "Link up to 10 Shopify stores",
        "Safe landing & store switcher",
      ],
      tip: "Use Stores to invite shops, assign countries, then Publish to all.",
    };
  }
  if (label === "Multi-store") {
    return {
      label,
      can: [
        "Ask first or send automatically (unlimited)",
        "Other websites + block countries",
        "Link up to 4 Shopify stores",
        "Safe landing & store switcher",
      ],
      tip: "Open Stores to generate a link code for your .co.uk (or other) shop.",
    };
  }
  if (label === "Starter") {
    return {
      label,
      can: [
        "Ask first or send automatically (unlimited)",
        "Send visitors to another website you own",
        "Block countries you don’t sell to",
        "Custom popup CSS + restore previous settings",
      ],
      tip: "Add a redirect under Where they go, or customize the popup.",
    };
  }
  return {
    label: "Free",
    can: [
      "Ask first via Shopify Markets (needs 2+ markets)",
      "Up to 300 automatic sends / month",
      "Popup in 10 languages · ads & SEO safe",
    ],
    tip: "Add a second market in Shopify (Settings → Markets), then Preview as that country.",
  };
}

export function isFreePlan(plan?: string, planKey?: string) {
  const label = planLabel(plan || planKey);
  return label === "Free";
}

/** Max shops in a Primary + connected set (includes Primary). null = linking not on plan. */
export function storeLimitForPlan(
  plan?: string,
  planKey?: string,
): number | null {
  const label = planLabel(plan || planKey);
  if (label === "Scale") return 10;
  if (label === "Multi-store") return 4;
  return null;
}

export function hasFeature(
  features: string[] | undefined,
  feature: string,
  opts?: { plan?: string; planKey?: string },
): boolean {
  if (Array.isArray(features) && features.length > 0) {
    return features.includes(feature);
  }
  // Fallback when entitlements missing: paid unlocks Starter+ surfaces.
  return !isFreePlan(opts?.plan, opts?.planKey) && feature !== "networkRules";
}

export function actionLabel(action: string) {
  if (action === "redirect") return "Sent automatically";
  if (action === "block") return "Blocked";
  return "Asked first";
}

export function parseCountries(raw: string): string[] {
  return raw
    .split(/[,]+/)
    .map((c) => c.trim())
    .filter(Boolean)
    .map((c) => {
      if (/^[a-z]{2}$/i.test(c)) return c.toUpperCase();
      const map: Record<string, string> = {
        "united kingdom": "GB",
        uk: "GB",
        ireland: "IE",
        germany: "DE",
        france: "FR",
        canada: "CA",
        australia: "AU",
      };
      return map[c.toLowerCase()] || c.toUpperCase().slice(0, 2);
    });
}

export function relativePublished(iso?: string | null) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
